/**
 * Lead Data Service
 *
 * 도입문의 퍼널 시스템의 leadData 관리 서비스
 *
 * SOLID 원칙 적용:
 * - Single Responsibility: 각 함수가 하나의 책임만 가짐
 * - Open/Closed: 함수 기반으로 확장 가능
 * - Dependency Inversion: sessionStorage 추상화
 *
 * leadData 구조:
 * {
 *   leadId: string,        // 필수: lead 고유 ID
 *   companyName: string,   // 필수: 매장명
 *   type: string,          // 필수: 업체 유형 (STORE | PRODUCT)
 *   roadAddress: string,   // 선택: 도로명 주소
 *   x: string,            // 선택: 경도
 *   y: string,            // 선택: 위도
 *   contact: string       // 선택: 연락처
 * }
 */

const STORAGE_KEY = 'leadData';

// ============================================
// Private Helper Functions (내부 함수)
// ============================================

/**
 * sessionStorage에서 데이터 조회 (private)
 * @returns {Object|null}
 */
function getFromStorage() {
  try {
    const data = sessionStorage.getItem(STORAGE_KEY);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.error('❌ [LeadDataService] Failed to get data from storage:', error);
    return null;
  }
}

/**
 * sessionStorage에 데이터 저장 (private)
 * @param {Object} data
 * @returns {boolean}
 */
function saveToStorage(data) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    return true;
  } catch (error) {
    console.error('❌ [LeadDataService] Failed to save data to storage:', error);
    return false;
  }
}

/**
 * sessionStorage에서 데이터 삭제 (private)
 * @returns {boolean}
 */
function removeFromStorage() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
    return true;
  } catch (error) {
    console.error('❌ [LeadDataService] Failed to remove data from storage:', error);
    return false;
  }
}

/**
 * leadData 유효성 검증 (private)
 * @param {Object} data
 * @returns {Object} { isValid: boolean, errors: string[] }
 */
function validate(data) {
  const errors = [];

  if (!data || typeof data !== 'object') {
    errors.push('Invalid data format');
    return { isValid: false, errors };
  }

  if (!data.leadId) {
    errors.push('leadId is required');
  }

  if (!data.companyName) {
    errors.push('companyName is required');
  }

  if (data.companyName && typeof data.companyName === 'string' && data.companyName.trim() === '') {
    errors.push('companyName cannot be empty string');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * 기존 leadData와 새 leadData 비교 (private)
 * @param {Object|null} existingData
 * @param {Object} newData
 * @returns {Object} { action: 'CREATE'|'REPLACE'|'UPDATE', isNewLead: boolean, previousLeadId: string|null }
 */
function compare(existingData, newData) {
  // Case 1: 기존 데이터 없음 → 생성
  if (!existingData || !existingData.leadId) {
    return {
      action: 'CREATE',
      isNewLead: true,
      previousLeadId: null,
    };
  }

  // Case 2: leadId 다름 → 새로운 퍼널 시작 (교체)
  if (existingData.leadId !== newData.leadId) {
    return {
      action: 'REPLACE',
      isNewLead: true,
      previousLeadId: existingData.leadId,
    };
  }

  // Case 3: leadId 같음 → 기존 퍼널 업데이트 (병합)
  return {
    action: 'UPDATE',
    isNewLead: false,
    previousLeadId: existingData.leadId,
  };
}

// ============================================
// Public API Functions (공개 함수)
// ============================================

/**
 * leadData 조회
 * @returns {Object|null}
 */
export function getLeadData() {
  return getFromStorage();
}

/**
 * leadData 저장 (leadId 비교 후 생성/교체/업데이트 결정)
 *
 * Single Responsibility: 저장 로직만 담당
 * Open/Closed: 비교 로직은 compare() 함수로 분리되어 확장 가능
 *
 * @param {Object} newLeadData - 저장할 leadData
 * @returns {Object} { isNewLead: boolean, previousLeadId: string|null, action: string }
 */
export function saveLeadDataSafely(newLeadData) {
  if (!newLeadData || !newLeadData.leadId) {
    console.warn('⚠️ [LeadDataService] Invalid leadData:', newLeadData);
    return { isNewLead: false, previousLeadId: null, action: 'NONE' };
  }
  const existingData = getFromStorage();
  const compareResult = compare(existingData, newLeadData);

  let dataToSave;

  switch (compareResult.action) {
    case 'CREATE':
      // 최초 생성
      dataToSave = newLeadData;
      break;

    case 'REPLACE':
      // 새로운 퍼널 시작 (완전 교체)
      dataToSave = newLeadData;
      break;

    case 'UPDATE':
      // 기존 퍼널 업데이트 (병합)
      dataToSave = { ...existingData, ...newLeadData };
      break;

    default:
      throw new Error(`Unknown action: ${compareResult.action}`);
  }

  const success = saveToStorage(dataToSave);

  if (!success) {
    throw new Error('Failed to save leadData to storage');
  }

  return {
    isNewLead: compareResult.isNewLead,
    previousLeadId: compareResult.previousLeadId,
    action: compareResult.action,
  };
}

/**
 * leadId 쿼리 파라미터 기반 leadData 복원
 *
 * ⚠️ CRITICAL: leadId 쿼리 = 플로우 식별자, NOT 데이터 전달 수단
 *
 * leadId 쿼리 존재 = 도입문의 퍼널 (Flow A)
 * → sessionStorage MUST have leadData with companyName
 * → 없으면 null 반환 (에러)
 *
 * leadId 쿼리 부재 = 직접 등록 플로우 (Flow B or C)
 * → null 반환 (정상)
 *
 * Single Responsibility: leadData 복원 로직만 담당
 * Dependency Inversion: storage 접근은 getFromStorage()로 추상화
 *
 * @param {Object} query - Vue Router query 객체
 * @returns {Object|null} leadData 또는 null
 */
export function restoreLeadData(query) {
  const hasLeadId = !!query.leadId;

  // Case 1: leadId 쿼리 없음 → 직접 등록 플로우 (정상)
  if (!hasLeadId) {
    return null;
  }

  // Case 2: leadId 쿼리 있음 → 도입문의 퍼널 (sessionStorage 필수)
  const leadData = getFromStorage();

  if (!leadData) {
    console.error('❌ [LeadDataService] leadId query exists but no sessionStorage leadData');
    console.error('❌ [LeadDataService] Invalid access or session expired');
    return null;
  }

  // leadData 유효성 검증
  const validationResult = validate(leadData);

  if (!validationResult.isValid) {
    console.error('❌ [LeadDataService] leadData validation failed:', validationResult.errors);
    return null;
  }

  // companyName 필수 확인 (도입문의 퍼널의 핵심 데이터)
  if (!leadData.companyName || !leadData.companyName.trim()) {
    console.error('❌ [LeadDataService] leadData missing companyName');
    console.error('❌ [LeadDataService] Data corrupted from LeadModal');
    return null;
  }

  return leadData;
}

/**
 * leadData 삭제
 * @returns {boolean}
 */
export function clearLeadData() {
  console.log('🗑️ [LeadDataService] Clearing leadData');
  return removeFromStorage();
}

/**
 * leadData 부분 업데이트
 * @param {Object} updates - 업데이트할 필드들 { key: value }
 * @returns {boolean}
 */
export function updateLeadData(updates) {
  const existingData = getFromStorage();

  if (!existingData) {
    console.error('❌ [LeadDataService] No existing data to update');
    return false;
  }

  const updatedData = { ...existingData, ...updates };
  return saveToStorage(updatedData);
}

/**
 * leadData 존재 여부 확인
 * @returns {boolean}
 */
export function leadDataExists() {
  return sessionStorage.getItem(STORAGE_KEY) !== null;
}

/**
 * leadData 유효성 검증 (저장소 조회 + 검증)
 * @returns {Object} { isValid: boolean, errors: string[] }
 */
export function validateLeadData() {
  const leadData = getFromStorage();

  if (!leadData) {
    return {
      isValid: false,
      errors: ['No leadData in storage'],
    };
  }

  return validate(leadData);
}

// Default export
export default {
  getLeadData,
  saveLeadDataSafely,
  restoreLeadData,
  clearLeadData,
  updateLeadData,
  leadDataExists,
  validateLeadData,
};
